import { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { 
  createCheckoutSession, 
  verifySubscriptionFromUrl, 
  hasActiveSubscription, 
  createCustomerPortalSession 
} from '../services/stripeService';

export const useSubscription = (user: User | null) => {
  const [hasPaidSubscription, setHasPaidSubscription] = useState(false);
  const [isLoadingPayment, setIsLoadingPayment] = useState(false);
  const [isLoadingManageSubscription, setIsLoadingManageSubscription] = useState(false);
  const [showSubscriptionSuccess, setShowSubscriptionSuccess] = useState(false);
  const [showSubscriptionCancel, setShowSubscriptionCancel] = useState(false);

  // Check subscription status whenever the user changes
  useEffect(() => {
    checkSubscriptionStatus();
  }, [user]);

  // Check for subscription status in URL on initial load
  useEffect(() => {
    const checkSubscriptionStatusFromUrl = async () => {
      // Get query parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const subscriptionStatus = urlParams.get('subscription');
      
      if (subscriptionStatus === 'success') {
        // Verify the subscription with the backend
        const result = await verifySubscriptionFromUrl();
        
        if (result.success) {
          // Show success message
          setShowSubscriptionSuccess(true);
          
          // Update subscription status
          setHasPaidSubscription(true);
          
          // Clear the URL parameters after processing
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } else if (subscriptionStatus === 'cancel') {
        // Show cancellation message
        setShowSubscriptionCancel(true);
        
        // Clear the URL parameters after processing
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };
    
    // Only run if user is logged in
    if (user) {
      checkSubscriptionStatusFromUrl();
    }
  }, [user]);

  // Function to check if the user has an active subscription
  const checkSubscriptionStatus = async () => {
    if (user) {
      const isSubscribed = await hasActiveSubscription();
      setHasPaidSubscription(isSubscribed);
    } else {
      setHasPaidSubscription(false);
    }
  };

  // Redirect to Stripe payment page
  const redirectToPayment = async () => {
    try {
      // Show loading state
      setIsLoadingPayment(true);
      
      // Create checkout session
      const { url } = await createCheckoutSession();
      
      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      setIsLoadingPayment(false);
    }
  };
  
  // Redirect to Stripe customer portal to manage subscription
  const redirectToManageSubscription = async () => {
    try {
      // Show loading state
      setIsLoadingManageSubscription(true);
      
      // Create customer portal session
      const { url } = await createCustomerPortalSession();
      
      // Redirect to Stripe customer portal
      window.location.href = url;
    } catch (error) {
      console.error('Failed to create customer portal session:', error);
      setIsLoadingManageSubscription(false);
    }
  };

  return {
    hasPaidSubscription,
    setHasPaidSubscription,
    isLoadingPayment,
    isLoadingManageSubscription,
    showSubscriptionSuccess,
    setShowSubscriptionSuccess,
    showSubscriptionCancel,
    setShowSubscriptionCancel,
    redirectToPayment,
    redirectToManageSubscription,
    checkSubscriptionStatus
  };
}; 